// imports react and next
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useDispatch } from "react-redux";

// imports redux
import store from "@/redux/store";
import {
  fetchLabelsAndConfigData,
  setLabelsConfigsData,
} from "@/redux/actions/labelsAndConfigActions";
import { fetchTicinoPageData } from "@/redux/actions/ticinoPageActions";

//import components
const MetaTags = dynamic(() => import("@/common/MetaTags"));
const MainSlider = dynamic(() => import("@/components/Sliders"));
const AbstractCard = dynamic(() => import("@/components/Card"));
const GridSection = dynamic(() => import("@/components/Section/GridSection"));
const StickyScrollBar = dynamic(() => import("@/components/StickyScrollBar"));
const IntroSectionInner = dynamic(() => import("@/components/Section/IntroSectionInner"));
const ContainerWithShadow = dynamic(() => import("@/components/Section/ContainerWithShadow"));
const Breadcrumbs = dynamic(() => import("@/components/Breadcrumbs"));

//import graphql queries
import { LABEL_AND_CONFIG_QUERY } from "@/graphql/settingsQuery";
import { TICINO_PAGE_QUERY } from "@/graphql/ticinoPageQuery";

// imports utilities
import { isEmpty } from "@/utils/functions";

const Ticino = (props) => {
  const { labelsConfigData, ticinoPageData } = props;

  const dispatch = useDispatch();

  const [siteConfig, setSiteConfig] = useState({});
  const [labelsList, setLabelsList] = useState({});

  useEffect(() => {
    // Set labelslist local state while changed on redux store data
    dispatch(setLabelsConfigsData(labelsConfigData));
    setLabelsList(labelsConfigData?.buttonsAndLabelsPage);
    setSiteConfig(labelsConfigData?.siteConfig);
  }, [dispatch, labelsConfigData]);

  return (
    <>
      <MetaTags
        metaTags={ticinoPageData?._seoMetaTags}
        seoData={ticinoPageData?.seo}
        noFollow={ticinoPageData?.nofollow}
        noIndex={ticinoPageData?.noindex}
      />
      <StickyScrollBar />

      <div className="relative w-full">
        <MainSlider sliderData={ticinoPageData?.gallery} siteConfig={siteConfig} />
      </div>

      <ContainerWithShadow
        containerWidthClass="container"
        noShadow={isEmpty(ticinoPageData?.ticinoSubpages)}
      >
        <Breadcrumbs
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: ticinoPageData?.title,
            },
          ]}
        />
        <IntroSectionInner title={ticinoPageData?.subtitle} text={ticinoPageData?.abstract} />
      </ContainerWithShadow>

      {!isEmpty(ticinoPageData?.ticinoSubpages) && (
        <GridSection
          containerWidthClass="w-full"
          containerGridClass="sm:grid-cols-2 xxs:grid-cols-1 sm:mx-[40px] lg:mx-0"
          containerGridGap="gap-6"
        >
          {ticinoPageData?.ticinoSubpages?.map((item, index) => (
            <AbstractCard
              key={index}
              bgImage={item?.gallery?.[0]}
              title={item?.title}
              abstract={item?.abstract}
              btnLabel={labelsList?.more}
              btnUrl={`/${item?.parentSlug?.slug}/${item?.slug}`}
              isOpen={"_self"}
              hideIcon={true}
            />
          ))}
        </GridSection>
      )}
    </>
  );
};

export default Ticino;

export async function getStaticProps({ preview = false, locale = "en" }) {
  let requestParams = TICINO_PAGE_QUERY?.replace(/\lang/g, locale);

  const ticinoResponse = await store.dispatch(fetchTicinoPageData(requestParams, preview));
  const labelsConfigData = await store.dispatch(
    fetchLabelsAndConfigData(LABEL_AND_CONFIG_QUERY, preview, { lang: locale }),
  );

  return {
    props: {
      preview,
      ticinoPageData: ticinoResponse,
      labelsConfigData: labelsConfigData || {},
    },
  };
}
